import { useState } from "react";
import { useNavigate } from "react-router";
import clsx from "clsx";
import { CheckCircle, LockSimple, CircleNotch, X } from "@phosphor-icons/react";
import { CurriculumModule } from "./types";
import { useAuth } from "../../hooks/useAuth";
import LockedModulePreviewModal from "./LockedModulePreviewModal";

const STATUS_LABELS: Record<CurriculumModule["status"], string> = {
  done: "Tamamlandı",
  now: "BURADASIN",
  available: "Açık",
  behind: "Yarım kaldı",
  soon: "Yakında",
  locked: "Kilitli",
  missed: "Geçti",
};

export default function RoadmapNode({
  mod,
  side,
}: {
  mod: CurriculumModule;
  side: "left" | "right";
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = !!user?.cohort_name && /demo/i.test(user.cohort_name);
  const isLockedModule = mod.status === "locked";

  const clickable =
    (mod.assigned && !isLockedModule) || (isDemo && isLockedModule);

  const [previewOpen, setPreviewOpen] = useState(false);

  const onClick = () => {
    if (!clickable) return;
    if (isLockedModule) {
      setPreviewOpen(true);
    } else {
      navigate(`/hepsi#module-${mod.id}`);
    }
  };

  const dot = (
    <div
      className={clsx(
        "w-9 h-9 rounded-full grid place-items-center text-sm font-bold shrink-0",
        mod.status === "done" && "bg-myGreen text-black",
        mod.status === "now" && "bg-myBlue text-white animate-pulse",
        mod.status === "available" && "bg-myBlue/15 text-myBlue border border-myBlue/40",
        mod.status === "behind" && "bg-myAmber/15 text-myAmber border border-myAmber/40",
        mod.status === "soon" && "bg-white/5 text-white/70 border border-white/15",
        mod.status === "locked" && "border border-dashed border-white/15 text-white/30",
        mod.status === "missed" && "bg-myAmber/15 text-myAmber border border-myAmber/40",
      )}
      style={mod.status === "now" ? { boxShadow: "0 0 0 5px rgba(0,102,200,0.2), 0 0 20px rgba(0,102,200,0.5)" } : undefined}
    >
      {mod.status === "done" && <CheckCircle weight="fill" size={20} />}
      {mod.status === "now" && <CircleNotch weight="bold" size={18} />}
      {mod.status === "available" && mod.order}
      {mod.status === "behind" && mod.order}
      {mod.status === "soon" && mod.order}
      {mod.status === "locked" && <LockSimple weight="bold" size={16} />}
      {mod.status === "missed" && <X weight="bold" size={16} />}
    </div>
  );

  const card = (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      aria-disabled={!clickable}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={clsx(
        "flex-1 p-3 rounded-xl border bg-surface-1 transition-all",
        clickable && "cursor-pointer hover:border-myBlue hover:-translate-y-0.5",
        !clickable && "cursor-not-allowed",
        mod.status === "done" && "border-myGreen/20",
        mod.status === "now" && "border-myBlue/50 bg-myBlue/5",
        mod.status === "available" && "border-myBlue/20",
        mod.status === "behind" && "border-myAmber/30 bg-myAmber/[0.05]",
        mod.status === "soon" && "border-white/10",
        mod.status === "locked" && "border-white/5",
        mod.status === "missed" && "border-myAmber/20 bg-myAmber/[0.04]",
      )}
    >
      <div className="text-[10px] uppercase tracking-wider text-white/40">
        Modül {mod.order}
        {mod.status === "now" && (
          <span className="ml-2 bg-myBlue text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
            BURADASIN
          </span>
        )}
        {mod.status === "available" && (
          <span className="ml-2 bg-myBlue/15 text-myBlue text-[9px] font-bold px-1.5 py-0.5 rounded border border-myBlue/30">
            AÇIK
          </span>
        )}
        {mod.status === "behind" && (
          <span className="ml-2 bg-myAmber/15 text-myAmber text-[9px] font-bold px-1.5 py-0.5 rounded border border-myAmber/30">
            YARIM KALDI
          </span>
        )}
        {mod.status === "locked" && (
          <span className="ml-1">{isDemo ? "👁" : "🔒"}</span>
        )}
      </div>
      <div
        className={clsx(
          "text-sm font-semibold mt-1",
          mod.status === "locked" ? "text-white/35" : "text-white",
        )}
      >
        {mod.name}
      </div>
      <div
        className={clsx(
          "text-[11px] mt-1",
          mod.status === "done" && "text-myGreen",
          mod.status === "now" && "text-myBlue font-semibold",
          mod.status === "available" && "text-myBlue/80",
          mod.status === "behind" && "text-myAmber font-semibold",
          mod.status === "soon" && "text-white/40",
          mod.status === "locked" && "text-white/25",
          mod.status === "missed" && "text-myAmber font-semibold",
        )}
      >
        {mod.status === "done" && mod.score !== null
          ? `✓ ${STATUS_LABELS.done} · ${mod.score} pt`
          : mod.status === "now"
          ? `${mod.completed_units} / ${mod.total_units} ders bitti · Devam et →`
          : mod.status === "available"
          ? `${mod.completed_units} / ${mod.total_units} ders bitti · Başla →`
          : mod.status === "behind"
          ? `${mod.completed_units} / ${mod.total_units} ders bitti · Yarım kaldı, tamamla →`
          : mod.status === "missed"
          ? `${mod.completed_units} / ${mod.total_units} · Tamamlanmadı`
          : STATUS_LABELS[mod.status]}
      </div>
      {mod.skills.length > 0 && (
        <div className={clsx("flex flex-wrap gap-1 mt-2", mod.status === "locked" && "opacity-40")}>
          {mod.skills.map((s) => (
            <span
              key={s}
              className={clsx(
                "text-[10px] px-2 py-0.5 rounded border",
                mod.status === "done" && "bg-myGreen/12 text-myGreen border-myGreen/25",
                mod.status === "now" && "bg-myBlue/15 text-myBlue border-myBlue/30",
                mod.status === "available" && "bg-myBlue/8 text-myBlue/80 border-myBlue/20",
                mod.status === "behind" && "bg-myAmber/12 text-myAmber border-myAmber/25",
                (mod.status === "soon" || mod.status === "locked" || mod.status === "missed") && "bg-white/5 text-white/50 border-white/10",
              )}
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  // Zigzag: left side puts dot on right of card, right side puts dot on left
  return (
    <>
      <div className={clsx("flex gap-3 my-3", side === "right" ? "pl-[40%]" : "pr-[40%]")}>
        {side === "left" ? (
          <>
            {card}
            {dot}
          </>
        ) : (
          <>
            {dot}
            {card}
          </>
        )}
      </div>
      {previewOpen && (
        <LockedModulePreviewModal mod={mod} onClose={() => setPreviewOpen(false)} />
      )}
    </>
  );
}
